import { DateTime } from 'luxon';
import { formatDateAsYYYYMMDD, parseYYYYMMDDToDate } from './date.utils';
import { Loan } from '../models';

/**
 * Utility class for loan-related calculations and operations
 */
export class LoanUtils {
  /**
   * Parse a formatted number string (e.g. "1,234.56") to a number
   * @param value The formatted number string
   * @returns The parsed number
   */
  static parseFormattedNumber(value: string | number | null | undefined): number {
    if (value === null || value === undefined) {
      return 0;
    }
    
    if (typeof value === 'number') {
      return value;
    }
    
    // Remove any non-numeric characters except decimal point
    const cleanedValue = value.toString().replace(/[^0-9.]/g, '');
    return parseFloat(cleanedValue) || 0;
  }
  
  /**
   * Calculate the monthly payment for a loan
   * @param loanAmount The loan amount
   * @param annualInterestRate The annual interest rate (as a percentage)
   * @param termInMonths The loan term in months
   * @returns The monthly payment amount
   */
  static calculateMonthlyPayment(loanAmount: number, annualInterestRate: number, termInMonths: number): number {
    // Convert annual interest rate to monthly decimal rate
    const monthlyRate = (annualInterestRate / 100) / 12;
    
    // If interest rate is 0, simple division
    if (monthlyRate === 0) {
      return loanAmount / termInMonths;
    }
    
    // Standard amortization formula: P = L[r(1+r)^n]/[(1+r)^n-1]
    const payment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, termInMonths)) / 
                    (Math.pow(1 + monthlyRate, termInMonths) - 1);
    
    // Round to 2 decimal places
    return Math.round(payment * 100) / 100;
  }
  
  /**
   * Calculate the payment amount for a loan based on frequency
   * @param loan The loan object
   * @returns The payment amount
   */
  static calculatePaymentAmount(loan: Loan): number {
    if (!loan.amount || !loan.interest_rate || !loan.term) {
      return 0;
    }
    
    const loanAmount = this.parseFormattedNumber(loan.amount);
    const annualInterestRate = this.parseFormattedNumber(loan.interest_rate);
    const termInMonths = this.parseFormattedNumber(loan.term);
    
    // Calculate the monthly payment
    const monthlyPayment = this.calculateMonthlyPayment(loanAmount, annualInterestRate, termInMonths);
    
    // Adjust payment based on frequency
    switch (loan.frequency) {
      case 'weekly':
        return Math.round((monthlyPayment * 12 / 52) * 100) / 100;
      case 'bi-weekly':
        return Math.round((monthlyPayment * 12 / 26) * 100) / 100;
      case 'monthly':
        return monthlyPayment;
      case 'quarterly':
        return Math.round((monthlyPayment * 3) * 100) / 100;
      case 'semi-annually':
        return Math.round((monthlyPayment * 6) * 100) / 100;
      case 'annually':
        return Math.round((monthlyPayment * 12) * 100) / 100;
      default:
        return monthlyPayment;
    }
  }
  
  /**
   * Calculate the total number of payments based on loan term and frequency
   * @param loan The loan object
   * @returns The total number of payments
   */
  static calculateTotalPayments(loan: Loan): number {
    if (!loan.term) {
      return 0;
    }
    
    const termInMonths = this.parseFormattedNumber(loan.term);
    
    // Convert term to total payments based on frequency
    switch (loan.frequency) {
      case 'weekly':
        return Math.round(termInMonths * 4.33); // Approximately 4.33 weeks per month
      case 'bi-weekly':
        return Math.round(termInMonths * 2.17); // Approximately 2.17 bi-weeks per month
      case 'monthly':
        return termInMonths;
      case 'quarterly':
        return Math.ceil(termInMonths / 3);
      case 'semi-annually':
        return Math.ceil(termInMonths / 6);
      case 'annually':
        return Math.ceil(termInMonths / 12);
      default:
        return termInMonths;
    }
  }
  
  /**
   * Calculate the next payment date based on the current payment date and frequency
   * @param currentDate The current payment date
   * @param frequency Payment frequency
   * @returns The next payment date as a string in YYYY-MM-DD format
   */
  /**
   * Calculate the next payment date based on the current date and payment frequency
   * and return it as a Date object
   * @param currentDate The current payment date
   * @param frequency Payment frequency
   * @returns The next payment date as a Date object
   */
  static calculateNextPaymentDateAsDate(currentDate: Date | string, frequency: string): Date {
    // Create a new date object to avoid modifying the original
    let date;
    if (typeof currentDate === 'string') {
      // Try to parse using parseYYYYMMDDToDate first for consistent handling
      const parsedDate = parseYYYYMMDDToDate(currentDate);
      date = parsedDate ? new Date(parsedDate) : new Date(currentDate);
    } else {
      date = new Date(currentDate);
    }
    
    // Set to noon to avoid timezone issues
    date.setHours(12, 0, 0, 0);
    
    // Store the original day of month to preserve it
    const originalDay = date.getDate();
    
    switch (frequency) {
      case 'annually':
        date.setFullYear(date.getFullYear() + 1);
        break;
      case 'semi-annually':
        date.setMonth(date.getMonth() + 6);
        break;
      case 'quarterly':
        date.setMonth(date.getMonth() + 3);
        break;
      case 'monthly':
        date.setMonth(date.getMonth() + 1);
        break;
      case 'bi-weekly':
        date.setDate(date.getDate() + 14);
        break;
      case 'weekly':
        date.setDate(date.getDate() + 7);
        break;
      default:
        // Default to monthly if frequency is not recognized
        date.setMonth(date.getMonth() + 1);
    }
    
    // Check if the day changed due to month rollover (e.g., Jan 31 -> Feb 28)
    // If so, set it back to the original day or the last day of the month
    if (frequency === 'monthly' || frequency === 'quarterly' || 
        frequency === 'semi-annually' || frequency === 'annually') {
      const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
      date.setDate(Math.min(originalDay, lastDayOfMonth));
    }
    
    // Reset to noon to ensure consistent time
    date.setHours(12, 0, 0, 0);
    
    return date;
  }
  
  static calculateNextPaymentDate(currentDate: Date | string, frequency: string): string {
    // Create a new date object to avoid modifying the original
    let date;
    if (typeof currentDate === 'string') {
      // Try to parse using parseYYYYMMDDToDate first for consistent handling
      const parsedDate = parseYYYYMMDDToDate(currentDate);
      date = parsedDate ? new Date(parsedDate) : new Date(currentDate);
    } else {
      date = new Date(currentDate);
    }
    
    // Store the original day of month to preserve it
    const originalDay = date.getDate();
    
    switch (frequency) {
      case 'annually':
        date.setFullYear(date.getFullYear() + 1);
        break;
      case 'semi-annually':
        date.setMonth(date.getMonth() + 6);
        break;
      case 'quarterly':
        date.setMonth(date.getMonth() + 3);
        break;
      case 'monthly':
        date.setMonth(date.getMonth() + 1);
        break;
      case 'bi-weekly':
        date.setDate(date.getDate() + 14);
        break;
      case 'weekly':
        date.setDate(date.getDate() + 7);
        break;
      default:
        // Default to monthly if frequency is not recognized
        date.setMonth(date.getMonth() + 1);
    }
    
    // Check if the day changed due to month rollover (e.g., Jan 31 -> Feb 28)
    // If so, set it back to the original day or the last day of the month
    if (frequency === 'monthly' || frequency === 'quarterly' || 
        frequency === 'semi-annually' || frequency === 'annually') {
      const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
      date.setDate(Math.min(originalDay, lastDayOfMonth));
    }
    
    // Use our Date object method and format the result
    const nextDate = this.calculateNextPaymentDateAsDate(currentDate, frequency);
    return formatDateAsYYYYMMDD(nextDate);
  }
  
  /**
   * Calculate the period interest rate based on the annual interest rate and payment frequency
   * @param annualInterestRate The annual interest rate (as a percentage)
   * @param frequency The payment frequency
   * @returns The period interest rate as a decimal
   */
  static calculatePeriodInterestRate(annualInterestRate: number, frequency: string): number {
    const annualRateDecimal = annualInterestRate / 100;
    
    switch (frequency) {
      case 'weekly':
        return annualRateDecimal / 52;
      case 'bi-weekly':
        return annualRateDecimal / 26;
      case 'monthly':
        return annualRateDecimal / 12;
      case 'quarterly':
        return annualRateDecimal / 4;
      case 'semi-annually':
        return annualRateDecimal / 2;
      case 'annually':
        return annualRateDecimal;
      default:
        return annualRateDecimal / 12; // Default to monthly
    }
  }
  
  /**
   * Calculate the payment breakdown for a specific payment number
   * @param loan The loan object
   * @param paymentNumber The payment number to calculate
   * @returns The payment breakdown (principal, interest, balance)
   */
  static calculatePaymentBreakdown(loan: Loan, paymentNumber: number): {
    principal: number;
    interest: number;
    balance: number;
    payment: number;
  } {
    if (!loan.amount || !loan.interest_rate || !loan.term) {
      return { principal: 0, interest: 0, balance: 0, payment: 0 };
    }
    
    const loanAmount = this.parseFormattedNumber(loan.amount);
    const annualInterestRate = this.parseFormattedNumber(loan.interest_rate);
    const regularPayment = loan.payment || this.calculatePaymentAmount(loan);
    
    // Calculate period interest rate
    const periodInterestRate = this.calculatePeriodInterestRate(annualInterestRate, loan.frequency);
    
    // Start with the original loan amount
    let balance = loanAmount;
    let interest = 0;
    let principal = 0;
    
    // Calculate for each payment up to the requested payment number
    for (let i = 1; i <= paymentNumber; i++) {
      // Calculate interest for this period
      interest = balance * periodInterestRate;
      interest = Math.round(interest * 100) / 100;
      
      // Calculate principal (payment minus interest)
      principal = regularPayment - interest;
      principal = Math.round(principal * 100) / 100;
      
      // If this is the last payment or principal is greater than balance,
      // adjust principal to match the remaining balance
      if (i === this.calculateTotalPayments(loan) || principal > balance) {
        principal = balance;
      }
      
      // Update balance for next iteration
      balance = Math.max(0, balance - principal);
      balance = Math.round(balance * 100) / 100;
    }
    
    return {
      principal,
      interest,
      balance,
      payment: principal + interest
    };
  }
  
  /**
   * Generate a complete amortization schedule for a loan
   * @param loan The loan object
   * @returns The amortization schedule
   */
  static generateAmortizationSchedule(loan: Loan): Array<{
    paymentNumber: number;
    paymentDate: Date;
    payment: number;
    principal: number;
    interest: number;
    balance: number;
    state: string;
  }> {
    if (!loan.amount || !loan.interest_rate || !loan.term) {
      return [];
    }
    
    const schedule = [];
    const loanAmount = this.parseFormattedNumber(loan.amount);
    const regularPayment = loan.payment || this.calculatePaymentAmount(loan);
    const totalPayments = this.calculateTotalPayments(loan);
    
    // Use the remaining balance if available, otherwise use the original loan amount
    // This ensures that extra payments are properly accounted for in future calculations
    let balance = loan.remaining_balance !== undefined && loan.remaining_balance !== null
      ? this.parseFormattedNumber(loan.remaining_balance)
      : loanAmount;
    
    // Add the initial funding row as payment number 0
    let fundingDate: Date;
    if (loan.close_date) {
      // Use parseYYYYMMDDToDate for date handling
      if (typeof loan.close_date === 'string') {
        const parsedDate = parseYYYYMMDDToDate(loan.close_date);
        fundingDate = parsedDate || new Date();
      } else {
        fundingDate = new Date(loan.close_date);
      }
    } else {
      fundingDate = new Date();
    }
    
    schedule.push({
      paymentNumber: 0,
      paymentDate: fundingDate,
      payment: -loanAmount, // Negative value for funding (money received)
      principal: 0,
      interest: 0,
      balance: loanAmount,
      state: 'Funding',
      isActual: false // Explicitly mark as not an actual payment
    });
    
    // Get the start payment date or next payment date if available
    let currentDate: Date;
    
    // If next_payment_date is available, use it as the starting point for future payments
    // This ensures we're starting from the correct point after any payments have been made
    if (loan.next_payment_date) {
      if (typeof loan.next_payment_date === 'string') {
        const parsedDate = parseYYYYMMDDToDate(loan.next_payment_date);
        currentDate = parsedDate || new Date();
      } else {
        currentDate = new Date(loan.next_payment_date);
      }
    } else if (loan.start_payment_date) {
      // Use parseYYYYMMDDToDate for date handling
      if (typeof loan.start_payment_date === 'string') {
        const parsedDate = parseYYYYMMDDToDate(loan.start_payment_date);
        currentDate = parsedDate || new Date(fundingDate);
      } else {
        currentDate = new Date(loan.start_payment_date);
      }
    } else {
      currentDate = new Date(fundingDate);
      
      // Add one period to the funding date based on frequency
      switch (loan.frequency) {
        case 'annually':
          currentDate.setFullYear(currentDate.getFullYear() + 1);
          break;
        case 'semi-annually':
          currentDate.setMonth(currentDate.getMonth() + 6);
          break;
        case 'quarterly':
          currentDate.setMonth(currentDate.getMonth() + 3);
          break;
        case 'monthly':
          currentDate.setMonth(currentDate.getMonth() + 1);
          break;
        case 'bi-weekly':
          currentDate.setDate(currentDate.getDate() + 14);
          break;
        case 'weekly':
          currentDate.setDate(currentDate.getDate() + 7);
          break;
        default:
          currentDate.setMonth(currentDate.getMonth() + 1);
      }
    }
    
    // Calculate period interest rate
    const periodInterestRate = this.calculatePeriodInterestRate(
      this.parseFormattedNumber(loan.interest_rate),
      loan.frequency
    );
    
    // Determine the starting payment number
    // If next_payment_number is available, use it as the starting point
    const startingPaymentNumber = loan.next_payment_number || 1;
    
    // Generate the amortization schedule
    for (let i = startingPaymentNumber; i <= totalPayments; i++) {
      // Calculate interest for this period based on current balance
      let interest = balance * periodInterestRate;
      interest = Math.round(interest * 100) / 100;
      
      // Calculate principal (payment minus interest)
      let principal = regularPayment - interest;
      principal = Math.round(principal * 100) / 100;
      
      // For the last payment, adjust the payment amount to match the remaining balance plus interest
      let payment = regularPayment;
      
      // If this is the last payment or principal is greater than balance,
      // adjust principal to match the remaining balance
      if (i === totalPayments || principal > balance) {
        principal = balance;
        payment = principal + interest;
      }
      
      // Add the payment to the schedule
      // Create a deep copy of the date to avoid reference issues
      const paymentDateStr = formatDateAsYYYYMMDD(currentDate);
      const paymentDate = parseYYYYMMDDToDate(paymentDateStr) || new Date();
      
      schedule.push({
        paymentNumber: i,
        paymentDate,
        payment,
        principal,
        interest,
        balance: Math.max(0, balance - principal),
        state: 'Scheduled',
        isActual: false // Explicitly mark as not an actual payment
      });
      
      // Update balance for next iteration
      balance = Math.max(0, balance - principal);
      balance = Math.round(balance * 100) / 100;
      
      // If balance is 0, we're done
      if (balance === 0) {
        break;
      }
      
      // Calculate the next payment date
      // Create a string representation of the date first to avoid reference issues
      const nextDateStr = this.calculateNextPaymentDate(currentDate, loan.frequency);
      // Then parse it back to a Date object
      currentDate = parseYYYYMMDDToDate(nextDateStr) || new Date();
    }
    
    return schedule;
  }
  
  /**
   * Update loan with next payment information
   * @param loan The loan object to update
   * @returns The updated loan object
   */
  static updateLoanNextPaymentInfo(loan: Loan): Loan {
    // Create a copy of the loan to avoid modifying the original
    const updatedLoan = { ...loan };
    
    // If no remaining balance, use the original loan amount
    if (!updatedLoan.remaining_balance) {
      updatedLoan.remaining_balance = updatedLoan.amount;
    }
    
    // Determine the next payment number
    let nextPaymentNumber = 1;
    
    // If we have transaction history, calculate based on that
    // This would be implemented by the component using this method
    
    // Calculate the payment breakdown for the next payment
    const breakdown = this.calculatePaymentBreakdown(updatedLoan, nextPaymentNumber);
    
    // Update the loan with the next payment information
    updatedLoan.next_payment_number = nextPaymentNumber;
    updatedLoan.next_principal = breakdown.principal;
    updatedLoan.next_interest = breakdown.interest;
    
    // Calculate the next payment date if not already set
    if (!updatedLoan.next_payment_date) {
      // If we have a start payment date, calculate from that
      if (updatedLoan.start_payment_date) {
        const startDate = typeof updatedLoan.start_payment_date === 'string' ? 
          new Date(updatedLoan.start_payment_date) : new Date(updatedLoan.start_payment_date);
        
        // Calculate the next payment date based on the payment number and frequency
        let nextDate = new Date(startDate);
        
        for (let i = 1; i < nextPaymentNumber; i++) {
          nextDate = new Date(this.calculateNextPaymentDate(nextDate, updatedLoan.frequency));
        }
        
        updatedLoan.next_payment_date = formatDateAsYYYYMMDD(nextDate);
      } else if (updatedLoan.close_date) {
        // If no start payment date, calculate from close date
        const closeDate = typeof updatedLoan.close_date === 'string' ? 
          new Date(updatedLoan.close_date) : new Date(updatedLoan.close_date);
        
        // Add one period to the close date based on frequency
        let nextDate = new Date(closeDate);
        
        switch (updatedLoan.frequency) {
          case 'annually':
            nextDate.setFullYear(nextDate.getFullYear() + 1);
            break;
          case 'semi-annually':
            nextDate.setMonth(nextDate.getMonth() + 6);
            break;
          case 'quarterly':
            nextDate.setMonth(nextDate.getMonth() + 3);
            break;
          case 'monthly':
            nextDate.setMonth(nextDate.getMonth() + 1);
            break;
          case 'bi-weekly':
            nextDate.setDate(nextDate.getDate() + 14);
            break;
          case 'weekly':
            nextDate.setDate(nextDate.getDate() + 7);
            break;
          default:
            nextDate.setMonth(nextDate.getMonth() + 1);
        }
        
        updatedLoan.next_payment_date = formatDateAsYYYYMMDD(nextDate);
      }
    }
    
    return updatedLoan;
  }
  
  /**
   * Process a loan payment and update the loan and transaction
   * @param loan The loan object
   * @param paymentAmount The payment amount
   * @param paymentDate The payment date
   * @param lateFee Optional late fee
   * @returns Object with updated loan and transaction
   */
  /**
   * Get the amortization payment for a specific payment number
   * This is a shared method used by both the amortization component and loan payment dialog
   * @param loan The loan object
   * @param paymentNumber The payment number to calculate
   * @param strictAmortization If true, calculate based on original loan amount regardless of current balance
   * @returns The payment details including principal, interest, and balance
   */
  static getAmortizationPayment(loan: Loan, paymentNumber: number, strictAmortization: boolean = true): {
    principal: number;
    interest: number;
    balance: number;
    payment: number;
  } {
    if (!loan.amount || !loan.interest_rate || !loan.term) {
      return { principal: 0, interest: 0, balance: 0, payment: 0 };
    }
    
    // If we're using strict amortization, we always calculate from the original loan amount
    // This ensures consistent values regardless of the current remaining balance
    if (strictAmortization) {
      return this.calculatePaymentBreakdown(loan, paymentNumber);
    }
    
    // Otherwise, calculate based on the current remaining balance
    const remainingBalance = loan.remaining_balance !== null ? loan.remaining_balance : loan.amount;
    const periodInterestRate = this.calculatePeriodInterestRate(loan.interest_rate, loan.frequency);
    const regularPayment = loan.payment || this.calculatePaymentAmount(loan);
    
    // Calculate interest for this period based on current balance
    const interest = remainingBalance * periodInterestRate;
    const roundedInterest = Math.round(interest * 100) / 100;
    
    // Calculate principal (payment minus interest)
    let principal = regularPayment - roundedInterest;
    principal = Math.round(principal * 100) / 100;
    
    // If principal is greater than remaining balance, adjust it
    if (principal > remainingBalance) {
      principal = remainingBalance;
    }
    
    // Calculate the new balance
    const balance = Math.max(0, remainingBalance - principal);
    
    return {
      principal,
      interest: roundedInterest,
      balance,
      payment: principal + roundedInterest
    };
  }

  static processLoanPayment(
    loan: Loan,
    paymentAmount: number,
    paymentDate: Date | string,
    lateFee: number = 0
  ): {
    updatedLoan: Loan;
    transaction: any;
  } {
    // Create a copy of the loan to avoid modifying the original
    const updatedLoan = { ...loan };
    
    // Ensure we have a remaining balance
    if (!updatedLoan.remaining_balance) {
      updatedLoan.remaining_balance = updatedLoan.amount;
    }
    
    // Get the next payment number
    const paymentNumber = updatedLoan.next_payment_number || 1;
    
    // Calculate the payment breakdown using our shared method
    const breakdown = this.getAmortizationPayment(updatedLoan, paymentNumber, true);
    
    // Determine principal and interest
    let principal = breakdown.principal;
    let interest = breakdown.interest;
    
    // If payment amount is different from the regular payment,
    // adjust the principal (extra payment goes to principal)
    if (paymentAmount > breakdown.payment) {
      principal += (paymentAmount - breakdown.payment);
    } else if (paymentAmount < breakdown.payment) {
      // If payment is less than expected, reduce principal
      principal = Math.max(0, paymentAmount - interest);
      
      // Ensure principal is not negative
      if (principal < 0) {
        interest = paymentAmount;
        principal = 0;
      }
    }
    
    // Ensure principal doesn't exceed remaining balance
    const remainingBalance = this.parseFormattedNumber(updatedLoan.remaining_balance);
    if (principal > remainingBalance) {
      principal = remainingBalance;
    }
    
    // Round to 2 decimal places
    principal = Math.round(principal * 100) / 100;
    interest = Math.round(interest * 100) / 100;
    
    // Create the transaction object
    const transaction = {
      bank_id: updatedLoan.bank_id,
      loan_id: updatedLoan.id,
      transaction_date: typeof paymentDate === 'string' ? paymentDate : formatDateAsYYYYMMDD(paymentDate),
      amount: paymentAmount,
      category: 'Loan payment',
      contact_id: updatedLoan.contact_id,
      principal,
      interest,
      late_fee: lateFee,
      loan_amortization_number: paymentNumber
    };
    
    // Update the loan's remaining balance
    updatedLoan.remaining_balance = Math.max(0, remainingBalance - principal);
    
    // Update the loan's last payment date
    if ('last_payment_date' in updatedLoan) {
      updatedLoan.last_payment_date = typeof paymentDate === 'string' ? paymentDate : formatDateAsYYYYMMDD(paymentDate);
    }
    
    // Calculate the next payment date
    // First get the next payment date as a Date object
    const nextPaymentDateObj = this.calculateNextPaymentDateAsDate(paymentDate, updatedLoan.frequency);
    // Then format it as YYYY-MM-DD to ensure proper timezone handling
    updatedLoan.next_payment_date = formatDateAsYYYYMMDD(nextPaymentDateObj);
    
    // Increment the next payment number
    updatedLoan.next_payment_number = paymentNumber + 1;
    
    // Calculate the next payment breakdown
    const nextBreakdown = this.getAmortizationPayment(updatedLoan, updatedLoan.next_payment_number, true);
    updatedLoan.next_principal = nextBreakdown.principal;
    updatedLoan.next_interest = nextBreakdown.interest;
    
    return {
      updatedLoan,
      transaction
    };
  }

  /**
   * Generate a payment schedule starting from a specific balance and date
   * This is useful for mid-life loans where we need to recalculate the schedule
   * from a point in time with a specific remaining balance
   * 
   * @param startingBalance The balance to start the schedule from
   * @param payment The payment amount
   * @param interestRate The annual interest rate (as a percentage)
   * @param termMonths The remaining term in months
   * @param startDate The date to start the schedule from
   * @param frequency The payment frequency
   * @returns The payment schedule
   */
  static generatePaymentSchedule(
    startingBalance: number,
    payment: number,
    interestRate: number,
    termMonths: number,
    startDate: Date,
    frequency: string,
    closeDate?: Date
  ): Array<{
    paymentNumber: number;
    paymentDate: Date;
    payment: number;
    principal: number;
    interest: number;
    remainingBalance: number;
    paid: boolean;
  }> {
    if (!startingBalance || !payment || interestRate === undefined || interestRate === null || !termMonths) {
      return [];
    }
    
    const schedule = [];
    
    // Calculate period interest rate
    const periodInterestRate = this.calculatePeriodInterestRate(interestRate, frequency);
    
    // Start with the provided balance
    let balance = startingBalance;
    let currentDate = new Date(startDate);
    
    // Determine the total number of payments based on frequency and term
    const paymentsPerYear = this.getPaymentsPerYear(frequency);
    const totalPayments = Math.ceil((termMonths / 12) * paymentsPerYear);
    
    // Add the initial funding row as payment number 0 if closeDate is provided
    if (closeDate) {
      schedule.push({
        paymentNumber: 0,
        paymentDate: new Date(closeDate),
        payment: startingBalance,
        principal: 0,
        interest: 0,
        remainingBalance: startingBalance,
        paid: true
      });
    }
    
    // Generate the payment schedule
    for (let i = 1; i <= totalPayments; i++) {
      // Calculate interest for this period
      const interestAmount = balance * periodInterestRate;
      const roundedInterest = Math.round(interestAmount * 100) / 100;
      
      // Calculate principal (payment minus interest)
      let paymentPrincipal = payment - roundedInterest;
      paymentPrincipal = Math.round(paymentPrincipal * 100) / 100;
      
      // For the last payment, adjust the payment amount to match the remaining balance plus interest
      let paymentAmount = payment;
      
      // If this is the last payment or principal is greater than balance,
      // adjust principal to match the remaining balance
      if (i === totalPayments || paymentPrincipal > balance) {
        paymentPrincipal = balance;
        paymentAmount = paymentPrincipal + roundedInterest;
      }
      
      // Calculate the new balance after this payment
      const newBalance = Math.max(0, balance - paymentPrincipal);
      const roundedBalance = Math.round(newBalance * 100) / 100;
      
      // Add the payment to the schedule
      schedule.push({
        paymentNumber: i,
        paymentDate: new Date(currentDate),
        payment: paymentAmount,
        principal: paymentPrincipal,
        interest: roundedInterest,
        remainingBalance: roundedBalance,
        paid: false
      });
      
      // Update balance for next iteration
      balance = roundedBalance;
      
      // If balance is 0, we're done
      if (balance === 0) {
        break;
      }
      
      // Calculate the next payment date
      switch (frequency) {
        case 'annually':
          currentDate = new Date(currentDate.setFullYear(currentDate.getFullYear() + 1));
          break;
        case 'semi-annually':
          currentDate = new Date(currentDate.setMonth(currentDate.getMonth() + 6));
          break;
        case 'quarterly':
          currentDate = new Date(currentDate.setMonth(currentDate.getMonth() + 3));
          break;
        case 'monthly':
          currentDate = new Date(currentDate.setMonth(currentDate.getMonth() + 1));
          break;
        case 'bi-weekly':
          currentDate = new Date(currentDate.setDate(currentDate.getDate() + 14));
          break;
        case 'weekly':
          currentDate = new Date(currentDate.setDate(currentDate.getDate() + 7));
          break;
        default:
          currentDate = new Date(currentDate.setMonth(currentDate.getMonth() + 1));
      }
    }
    
    return schedule;
  }

  /**
   * Get number of payments per year based on frequency
   * @param frequency The payment frequency
   * @returns The number of payments per year
   */
  static getPaymentsPerYear(frequency: string): number {
    switch (frequency) {
      case 'annually': return 1;
      case 'semi-annually': return 2;
      case 'quarterly': return 4;
      case 'monthly': return 12;
      case 'bi-weekly': return 26;
      case 'weekly': return 52;
      default: return 12; // Default to monthly
    }
  }

  /**
   * Calculate the Volume Rate of Return (VRR) for a loan
   * @param principal The loan principal amount
   * @param payment The payment amount
   * @param termMonths The loan term in months
   * @param startDate The start date of the loan
   * @param closeDate The close date of the loan
   * @param frequency The payment frequency
   * @returns The VRR as a percentage
   */
  /**
   * Convert term in frequency units to months
   * @param term The term in frequency units
   * @param frequency The payment frequency
   * @returns The term in months
   */
  static convertTermToMonths(term: number, frequency: string): number {
    switch (frequency) {
      case 'annually': return term * 12;
      case 'semi-annually': return term * 6;
      case 'quarterly': return term * 3;
      case 'monthly': return term;
      case 'bi-weekly': return Math.round(term * (12 / 26));
      case 'weekly': return Math.round(term * (12 / 52));
      default: return term;
    }
  }

  static calculateVRR(
    principal: number,
    payment: number,
    termMonths: number,
    startDate: Date,
    closeDate: Date,
    frequency: string
  ): number {
    if (!principal || principal <= 0 || !payment || payment <= 0 || !termMonths || termMonths <= 0) {
      return 0;
    }

    // Calculate the interest rate that would result in this payment amount
    // using a binary search approach
    let minRate = 0;
    let maxRate = 100;
    let guessRate = 5; // Start with a reasonable guess
    let iterations = 0;
    const maxIterations = 20; // Prevent infinite loops
    const tolerance = 0.0001; // Acceptable error margin

    while (iterations < maxIterations) {
      // Calculate payment with current guess rate
      const periodRate = this.calculatePeriodInterestRate(guessRate, frequency);
      const paymentsPerYear = this.getPaymentsPerYear(frequency);
      const totalPayments = Math.ceil((termMonths / 12) * paymentsPerYear);
      
      // Calculate payment using the formula: P = L[r(1+r)^n]/[(1+r)^n-1]
      const calculatedPayment = principal * 
        (periodRate * Math.pow(1 + periodRate, totalPayments)) / 
        (Math.pow(1 + periodRate, totalPayments) - 1);
      
      // Check if we're close enough
      if (Math.abs(calculatedPayment - payment) < tolerance) {
        break;
      }
      
      // Adjust our guess based on whether payment is too high or too low
      if (calculatedPayment > payment) {
        maxRate = guessRate;
        guessRate = (minRate + guessRate) / 2;
      } else {
        minRate = guessRate;
        guessRate = (guessRate + maxRate) / 2;
      }
      
      iterations++;
    }
    
    // Now we have the approximate interest rate, calculate the total interest
    const paymentsPerYear = this.getPaymentsPerYear(frequency);
    const totalPayments = Math.ceil((termMonths / 12) * paymentsPerYear);
    const totalPaid = payment * totalPayments;
    const totalInterest = totalPaid - principal;
    
    // Calculate VRR: (total interest / principal) * 100
    return (totalInterest / principal) * 100;
  }
}
